import { useTranslation } from "@redotech/redo-i18n";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import * as NodeCSS from "./node.module.css";

export interface RecoverAnalyticsBlockProps {
  initialSends: number;
  uniqueCustomerReplies: number;
  unsubscribes: number;
  recoveredOrders: number;
  recoveredRevenue: number;
  skippedDueToUnsubscribe: number;
  skippedDueToSkipConditions: number;
}

export const RecoverAnalyticsBlock = memo(function RecoverAnalyticsBlock({
  initialSends,
  uniqueCustomerReplies,
  unsubscribes,
  recoveredOrders,
  recoveredRevenue,
  skippedDueToUnsubscribe,
  skippedDueToSkipConditions,
}: RecoverAnalyticsBlockProps) {
  const { t } = useTranslation();
  const totalSkipped = skippedDueToUnsubscribe + skippedDueToSkipConditions;
  const replyRate =
    initialSends > 0 ? (uniqueCustomerReplies / initialSends) * 100 : 0;
  const conversionRate =
    initialSends > 0 ? (recoveredOrders / initialSends) * 100 : 0;
  const averageOrderValue =
    recoveredOrders > 0 ? recoveredRevenue / recoveredOrders : 0;

  return (
    <Flex className={NodeCSS.analytics}>
      <Flex dir="column" w="full">
        <Flex justify="space-between">
          <Text fontSize="xs">{t("Sent")}</Text>
          <Text fontSize="xs" fontWeight="semibold">
            {initialSends > 0 ? initialSends.toLocaleString() : "-"}
          </Text>
        </Flex>
        <Flex justify="space-between">
          <Text fontSize="xs">{t("Replies")}</Text>
          <Text fontSize="xs" fontWeight="semibold">
            {uniqueCustomerReplies > 0
              ? uniqueCustomerReplies.toLocaleString()
              : "-"}
          </Text>
        </Flex>
        <Flex justify="space-between">
          <Text fontSize="xs">{t("Reply rate")}</Text>
          <Text fontSize="xs" fontWeight="semibold">
            {initialSends > 0
              ? `${replyRate.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
              : "-"}
          </Text>
        </Flex>
        <Flex justify="space-between">
          <Text fontSize="xs">{t("Conversion rate")}</Text>
          <Text fontSize="xs" fontWeight="semibold">
            {initialSends > 0
              ? `${conversionRate.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
              : "-"}
          </Text>
        </Flex>
        <Flex justify="space-between">
          <Text fontSize="xs">{t("Recovered revenue")}</Text>
          <Text fontSize="xs" fontWeight="semibold">
            {recoveredRevenue > 0
              ? `$${recoveredRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : "-"}
          </Text>
        </Flex>
        <Flex justify="space-between">
          <Text fontSize="xs">{t("Recovered orders")}</Text>
          <Text fontSize="xs" fontWeight="semibold">
            {recoveredOrders > 0 ? recoveredOrders.toLocaleString() : "-"}
          </Text>
        </Flex>
        <Flex justify="space-between">
          <Text fontSize="xs">{t("AOV")}</Text>
          <Text fontSize="xs" fontWeight="semibold">
            {recoveredOrders > 0
              ? `$${averageOrderValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : "-"}
          </Text>
        </Flex>
        <Flex justify="space-between">
          <Text fontSize="xs">{t("Unsubscribed")}</Text>
          <Text fontSize="xs" fontWeight="semibold">
            {unsubscribes > 0 ? unsubscribes.toLocaleString() : "-"}
          </Text>
        </Flex>
        <Flex borderColor="primary" borderStyle="solid" borderTopWidth="1px" />
        <Flex justify="space-between">
          <Text fontSize="xs">{t("Skipped")}</Text>
          <Text fontSize="xs" fontWeight="semibold">
            {totalSkipped > 0 ? totalSkipped.toLocaleString() : "-"}
          </Text>
        </Flex>
        <Flex justify="space-between" pl="lg">
          <Text className={NodeCSS.secondaryMetric} fontSize="xs">
            {t("Not subscribed")}
          </Text>
          <Text fontSize="xs" fontWeight="semibold">
            {skippedDueToUnsubscribe > 0
              ? skippedDueToUnsubscribe.toLocaleString()
              : "-"}
          </Text>
        </Flex>
        <Flex justify="space-between" pl="lg">
          <Text className={NodeCSS.secondaryMetric} fontSize="xs">
            {t("Filtered")}
          </Text>
          <Text fontSize="xs" fontWeight="semibold">
            {skippedDueToSkipConditions > 0
              ? skippedDueToSkipConditions.toLocaleString()
              : "-"}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
});
